import { message } from 'antd'
import { callApi } from './callApi'
import { markCallOrderEnded } from './callOrderLifecycleGuard'

const ORDER_RELEASE_RETRY_DELAY_MS = 1500
const pendingEndedOrderReleases = new Map<string, { reasonCode: string; warned: boolean; retryTimer: ReturnType<typeof setTimeout> | null }>()
const inFlightEndedOrderReleases = new Map<string, Promise<void>>()
const endedOrderReleaseReasons = new Map<string, string>()
const releasedEndedOrders = new Set<string>()

export async function releaseEndedOrder(orderID: string, reasonCode: string) {
  if (!orderID) {
    return
  }
  markCallOrderEnded(orderID)
  rememberEndedOrderReleaseReason(orderID, reasonCode)
  if (releasedEndedOrders.has(orderID)) {
    return
  }
  const inFlight = inFlightEndedOrderReleases.get(orderID)
  if (inFlight) {
    return inFlight
  }
  const releasePromise = performEndedOrderRelease(orderID, reasonCode).finally(() => {
    inFlightEndedOrderReleases.delete(orderID)
  })
  inFlightEndedOrderReleases.set(orderID, releasePromise)
  return releasePromise
}

export async function releaseEndedOrderForManualAnchorHangup(orderID: string) {
  if (!orderID) {
    return
  }
  rememberEndedOrderReleaseReason(orderID, 'anchor_hangup')
  if (releasedEndedOrders.has(orderID)) {
    return
  }
  const inFlight = inFlightEndedOrderReleases.get(orderID)
  if (inFlight) {
    return inFlight
  }
  const releasePromise = callApi.releaseOrder(orderID, {
    event_type: 'call_ended',
    reason_code: 'anchor_hangup',
  }).then(() => {
    markCallOrderEnded(orderID)
    releasedEndedOrders.add(orderID)
    clearPendingRelease(orderID)
  }).finally(() => {
    inFlightEndedOrderReleases.delete(orderID)
  })
  inFlightEndedOrderReleases.set(orderID, releasePromise)
  return releasePromise
}

async function performEndedOrderRelease(orderID: string, reasonCode: string) {
  const payload = {
    event_type: 'call_ended' as const,
    reason_code: reasonCode,
  }
  try {
    await callApi.releaseOrder(orderID, payload)
    releasedEndedOrders.add(orderID)
    clearPendingRelease(orderID)
  } catch {
    const pending = pendingEndedOrderReleases.get(orderID)
    pendingEndedOrderReleases.set(orderID, {
      reasonCode,
      warned: pending?.warned || false,
      retryTimer: pending?.retryTimer || null,
    })
    scheduleEndedOrderReleaseRetry(orderID)
  }
}

export function getPendingEndedOrderReleaseID() {
  return pendingEndedOrderReleases.keys().next().value || ''
}

export function shouldSkipUnloadRelease(orderID: string) {
  return Boolean(orderID && (releasedEndedOrders.has(orderID) || endedOrderReleaseReasons.get(orderID) === 'user_hangup'))
}

async function retryEndedOrderRelease(orderID: string) {
  const pending = pendingEndedOrderReleases.get(orderID)
  if (!pending || releasedEndedOrders.has(orderID)) {
    return
  }
  const inFlight = inFlightEndedOrderReleases.get(orderID)
  if (inFlight) {
    await inFlight
    return
  }
  const retryPromise = performEndedOrderReleaseRetry(orderID, pending.reasonCode).finally(() => {
    inFlightEndedOrderReleases.delete(orderID)
  })
  inFlightEndedOrderReleases.set(orderID, retryPromise)
  await retryPromise
}

async function performEndedOrderReleaseRetry(orderID: string, reasonCode: string) {
  const pending = pendingEndedOrderReleases.get(orderID)
  if (!pending) {
    return
  }
  try {
    await callApi.releaseOrder(orderID, {
      event_type: 'call_ended',
      reason_code: reasonCode,
    })
    releasedEndedOrders.add(orderID)
    clearPendingRelease(orderID)
  } catch {
    if (!pending.warned) {
      pendingEndedOrderReleases.set(orderID, { ...pending, warned: true, retryTimer: null })
      message.warning('Failed to release anchor status. It will retry when the network recovers.')
    } else {
      pendingEndedOrderReleases.set(orderID, { ...pending, retryTimer: null })
    }
    scheduleEndedOrderReleaseRetry(orderID)
  }
}

function scheduleEndedOrderReleaseRetry(orderID: string) {
  const pending = pendingEndedOrderReleases.get(orderID)
  if (!pending || pending.retryTimer !== null || releasedEndedOrders.has(orderID)) {
    return
  }
  const retryTimer = setTimeout(() => {
    const latest = pendingEndedOrderReleases.get(orderID)
    if (latest) {
      pendingEndedOrderReleases.set(orderID, { ...latest, retryTimer: null })
    }
    void retryEndedOrderRelease(orderID)
  }, ORDER_RELEASE_RETRY_DELAY_MS)
  pendingEndedOrderReleases.set(orderID, { ...pending, retryTimer })
}

export function resetEndedOrderReleaseState() {
  pendingEndedOrderReleases.forEach((pending) => {
    if (pending.retryTimer !== null) {
      clearTimeout(pending.retryTimer)
    }
  })
  pendingEndedOrderReleases.clear()
  inFlightEndedOrderReleases.clear()
  endedOrderReleaseReasons.clear()
  releasedEndedOrders.clear()
}

function clearPendingRelease(orderID: string) {
  const pending = pendingEndedOrderReleases.get(orderID)
  if (pending?.retryTimer !== null && pending?.retryTimer !== undefined) {
    clearTimeout(pending.retryTimer)
  }
  pendingEndedOrderReleases.delete(orderID)
}

function rememberEndedOrderReleaseReason(orderID: string, reasonCode: string) {
  if (!orderID || !reasonCode) {
    return
  }
  if (endedOrderReleaseReasons.has(orderID)) {
    return
  }
  endedOrderReleaseReasons.set(orderID, reasonCode)
}

export function retryPendingEndedOrderReleases() {
  pendingEndedOrderReleases.forEach((_, orderID) => {
    void retryEndedOrderRelease(orderID)
  })
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', retryPendingEndedOrderReleases)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      retryPendingEndedOrderReleases()
    }
  })
}
